import { useEffect, useMemo, useState } from 'react';
import { crazyEightsHandValue, sortHand, type CrazyEightsLegal, type CrazyEightsView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, string> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';

export function CrazyEightsTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as CrazyEightsView;
  const legal = useGame((s) => s.legal) as CrazyEightsLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  /** An eight waiting for its suit to be named. */
  const [eight, setEight] = useState<string | null>(null);
  const narrow = useNarrow();
  const pileSize = narrow ? 'md' : 'lg';
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const playable = new Set(myTurn ? (legal?.playable ?? []) : []);
  const handOver = view.phase === 'ROUND_END' || view.phase === 'MATCH_END';
  const last = view.roundResults.at(-1);

  // A new turn or deal clears a half-made choice.
  useEffect(() => {
    setEight(null);
  }, [view.round, view.turn, view.phase]);

  const onCard = (c: string) => {
    if (!myTurn || !playable.has(c) || acting) return;
    if (c[0] === '8') setEight(eight === c ? null : c);
    else void act({ type: 'play', card: c });
  };

  const topRed = isRedSuit(view.activeSuit);

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.scores[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {s !== mySeat && !handOver && <span className="tag">{view.handCounts[s]}</span>}
                {s !== mySeat && !handOver && (view.lacks[s] ?? []).length > 0 && (
                  <span className="tag" title={t('ce.lacks')}>
                    ✕ {(view.lacks[s] ?? []).map((x) => SUIT_SYMBOL[x]).join('')}
                  </span>
                )}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })} · {t('ce.goal', { n: view.config.targetScore })}
            {!myTurn && !handOver && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          {handOver && last ? (
            <div className="card glass" style={{ textAlign: 'center', maxHeight: '52vh', overflow: 'auto' }} role="status">
              <strong>
                {last.winner === null ? t('ce.blockedNone') : last.blocked ? t('ce.blocked', { name: name(last.winner) }) : t('ce.out', { name: name(last.winner) })}
              </strong>
              {last.winner !== null && <span className="pill accent" style={{ marginLeft: 8 }}>+{last.gains[last.winner]}</span>}
              {last.hands.map((h, s) =>
                s === last.winner && h.length === 0 ? null : (
                  <div key={s} style={{ marginTop: 6 }}>
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {name(s)} · {crazyEightsHandValue(h)}
                    </div>
                    <div className="melds" style={{ justifyContent: 'center' }}>
                      <div className="meld">
                        {h.map((c) => (
                          <PlayingCard key={c} id={c} size="sm" />
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          ) : (
            <div className="row" style={{ gap: 18, alignItems: 'flex-end', justifyContent: 'center' }}>
              <button
                className="col"
                style={{ alignItems: 'center', gap: 4, background: 'none', border: 0 }}
                disabled={!myTurn || !legal?.canDraw || acting}
                onClick={() => void act({ type: 'draw' })}
                aria-label={t('mr.drawStock')}
              >
                <PlayingCard faceDown size={pileSize} className={myTurn && legal?.canDraw ? 'playable' : ''} />
                <span className="center-note">{t('mr.stock', { n: view.stockCount })}</span>
              </button>
              <div className="col" style={{ alignItems: 'center', gap: 4 }}>
                <PlayingCard id={view.discardTop} size={pileSize} />
                <span className="center-note" style={{ color: topRed ? '#ff8a8a' : undefined }} aria-label={`${t('ce.suit')} ${SUIT_NAME[view.activeSuit]}`}>
                  {t('ce.suit')} {SUIT_SYMBOL[view.activeSuit]}
                </span>
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.scores[mySeat] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        <div className="action-bar">
          {eight && (
            <div className="bid-picker" role="group" aria-label={t('ce.nameSuit')}>
              <div className="bid-head">
                <strong>{t('ce.nameSuit')}</strong>
              </div>
              <div className="bid-chips" role="group">
                {(['S', 'H', 'D', 'C'] as const).map((s) => (
                  <button
                    key={s}
                    className="bid-chip"
                    style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
                    disabled={acting}
                    aria-label={SUIT_NAME[s]}
                    onClick={() => void act({ type: 'play', card: eight, suit: s }).finally(() => setEight(null))}
                  >
                    {SUIT_SYMBOL[s]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {myTurn && legal?.canPass && (
            <button className="btn" disabled={acting} onClick={() => void act({ type: 'pass' })}>
              {t('ta.pass')}
            </button>
          )}
          {myTurn && legal?.canDraw && (
            <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'draw' })}>
              {t('ce.draw', { n: view.draws + 1, max: view.config.maxDraws })}
            </button>
          )}
        </div>
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={view.hand.length === view.config.handSize ? i : undefined}
              selected={eight === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal !== null && !playable.has(c) && playable.size > 0}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 80 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((r) => (
              <tr key={r.round}>
                <td>R{r.round}</td>
                {r.gains.map((g, s) => (
                  <td key={s} className="num" style={{ color: r.winner === s ? 'var(--accent)' : undefined }}>
                    {g > 0 ? `+${g}` : '–'}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ce.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
